import React, { useState, useEffect } from 'react';
import { useForm, Resolver, FieldErrors } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useLogin } from '@features/auth/lib/useLogin';
import { useLayout } from 'src/layouts/LayoutContext';

import { Button } from '@components/ui/button';
import { FormField, FormItem, FormControl } from '@components/ui/form';
import { Form } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { buildLogin, fillWith } from 'src/devtools/fillers/auth';

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginFormErrors {
  email?: {
    type: string;
    message: string;
  };
  password?: {
    type: string;
    message: string;
  };
}

interface LoginFormProps {
  /**
   * Overrides the default post-login navigate (B2C → `/`). The W2 employee flow
   * passes its enroll step so this exact branded screen is reused in the
   * tokenized flow without leaving it.
   */
  onAuthenticated?: () => void;
  /** "Forgot your password?" target (default B2C `/forgot-password`). The W2
   *  flow passes its sibling `/e/:employerToken/forgot` — master F3 / risk R1. */
  forgotPasswordPath?: string;
  /** "Register here." target (default B2C `/registration`). */
  registrationPath?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onAuthenticated,
  forgotPasswordPath = '/forgot-password',
  registrationPath = '/registration',
}) => {
  const { login, error, isLoading, clear } = useLogin(onAuthenticated);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const { setMainClassName } = useLayout();
  const [searchParams] = useSearchParams();
  // Returning-employee variant: when login is the gate to the W2 plan-selection
  // surface (`/login?next=/select-plans`, Risk R8), show the "Welcome Back!"
  // benefits-selection copy instead of the default B2C applicant copy.
  const isSelectionReturn = searchParams.get('next') === '/select-plans';

  useEffect(() => {
    setMainClassName('max-md:min-h-[calc(75vh-156px)]');

    return () => {
      setMainClassName('');
    };
  }, [setMainClassName]);
  const form = useForm<LoginFormData>({
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: async (
      data: LoginFormData
    ): Promise<{
      values: LoginFormData;
      errors: FieldErrors<LoginFormData>;
    }> => {
      const errors: LoginFormErrors = {};
      // Validate email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.email = { type: 'pattern', message: 'Invalid email address.' };
      }
      // Validate password
      if (!data.password) {
        errors.password = {
          type: 'required',
          message: 'Password is required.',
        };
      }
      return { values: data, errors };
    },
  });

  const {
    formState: { errors },
    clearErrors,
  } = form;

  useEffect(() => {
    setDisabledSubmit(!!Object.keys(errors).length);
  }, [JSON.stringify(errors)]);

  const onSubmit = async (data: LoginFormData) => {
    await login(data);
  };

  return (
    <AuthLayout
      title={isSelectionReturn ? 'Welcome Back!' : 'Welcome back!'}
      description={
        isSelectionReturn
          ? // Client-final "returning to select plans" copy (VERBATIM, 2026-07-15).
            "We're glad to see you again. Log in to review your coverage options or make your selections. Your progress is saved, so you can log out and come back whenever you wish."
          : 'We’re glad to see you again. Log in to continue your application, review your coverage options, or make any updates. Your progress is saved, so you can jump right back in whenever you’re ready!'
      }
      variant="login"
      titleClassName="text-[40px] max-md:text-[28px]"
      descriptionClassName="text-xl mt-3.5 max-md:text-[14px] xl:max-w-[80%] max-2xl:max-w-[80%] max-2xl:max-w-full"
      headerClassName="2xl:pt-19.5 2xl:pl-7 max-lg:pb-0 max-md:min-h-83"
      contentClassName="2xl:pt-45.5"
    >
      <div className="w-full max-w-[430px] 2xl:max-w-[350px] 3xl:max-w-[430px]">
        {DEVFILL_ENABLED && (
          <DevFillButton
            testid={DEVFILL_TID.login}
            label="Fill login"
            onFill={() => fillWith(buildLogin, (v) => form.reset(v))}
          />
        )}
        <h2 className="text-4xl font-extrabold mb-4">Login</h2>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full"
            data-testid="login-form"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="email"
                      label="Email address"
                      type="email"
                      data-testid="login-email"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('email');
                        clear();
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputClass="w-full max-w-107.5 h-16 mb-5 rounded-lg border border-[#B7B7B7] p-2"
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-error text-sm -mt-4 mb-4">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="password"
                      label="Password"
                      type="password"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('password');
                        clear();
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      data-testid="login-password"
                      inputClass="w-full h-16 border rounded-lg border-[#B7B7B7] p-2 max-w-107.5 max-sm:max-w-full"
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-error text-sm mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />

            {error && (
              <p
                data-testid="login-error"
                className="text-error mt-7 -mb-7 text-sm font-medium"
              >
                {error}
              </p>
            )}

            <div className="flex flex-col items-start">
              <div className="flex w-full items-end 2xl:items-start 3xl:items-end max-md:flex-col 2xl:flex-col 3xl:flex-row">
                <Button
                  type="submit"
                  data-testid="login-submit"
                  className="w-full bg-primary disabled:text-[#ffffff55] disabled:opacity-100 h-14.5 max-w-68 text-white text-[19px] font-extrabold py-2 mt-12.5 rounded-full max-md:max-w-full"
                  disabled={disabledSubmit || isLoading}
                >
                  {isLoading ? 'Logging in...' : 'SUBMIT'}
                </Button>
                <Link
                  to={forgotPasswordPath}
                  className="text-base xs:text-xs max-md:mt-4 text-primary mr-1 mb-5 text-center md:text-right 2xl:text-left 3xl:text-right tracking-wide font-extrabold hover:underline mt-2 w-full max-w-[430px]"
                >
                  Forgot your password?
                </Link>
              </div>
              <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
                New user?{' '}
                <Link
                  to={registrationPath}
                  className="text-primary hover:underline whitespace-nowrap"
                >
                  Register here.
                </Link>
              </p>
            </div>
          </form>
        </Form>
      </div>
    </AuthLayout>
  );
};
